/* journey-collect.js — Barnaby first-party journey collector.
 *
 * Posts granular, NON-PII events to the dashboard's collector so Customer Journey
 * Intelligence can build real per-visitor timelines. A random first-party visitor id
 * (bb_vid) is stored in localStorage and attached to the cart so order-webhook can
 * stitch the journey to the order at purchase.
 *
 * Auto-captures: page views + scroll-depth milestones. The theme fires the rest via
 *   window.bbTrack(type, name, value)
 * e.g. bbTrack('builder','start'), bbTrack('personalise','skipped'),
 *      bbTrack('flavour','remove','Hazelnut'), bbTrack('checkout','reached'),
 *      bbTrack('wallet','apple_pay'), bbTrack('purchase','done').
 *
 * Privacy: only whitelisted event types + short strings are sent — never names,
 * messages, emails or personalisation content. Identity is resolved server-side.
 */
(function () {
  var ENDPOINT = (window.BB_COLLECT_URL) || 'https://barnabydashboard.netlify.app/.netlify/functions/journey-collect';
  var VID_KEY = 'bb_vid';

  function uid() {
    try { if (window.crypto && crypto.randomUUID) return crypto.randomUUID(); } catch (e) {}
    return 'v-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }
  function vid() {
    var v;
    try { v = localStorage.getItem(VID_KEY); if (!v) { v = uid(); localStorage.setItem(VID_KEY, v); } }
    catch (e) { v = window.__bbVid || (window.__bbVid = uid()); }
    return v;
  }

  var queue = [];
  var flushTimer = null;
  var attached = false;

  // First-touch attribution for the per-(ad,variant) funnel. Read at FLUSH time (not load) so the ad
  // randomiser has stamped bb_ad_variant by then. The server whitelists these keys and keeps the FIRST
  // attribution it sees per visitor, so sending it on every flush is safe and self-healing.
  function bbAttribution() {
    var p; try { p = new URLSearchParams(location.search); } catch (e) { p = { get: function () { return ''; } }; }
    function ss(k) { try { return sessionStorage.getItem(k) || localStorage.getItem(k) || ''; } catch (e) { return ''; } }
    var click = '';
    ['gclid', 'fbclid', 'msclkid', 'ttclid'].forEach(function (k) { if (!click && p.get(k)) click = k; });
    return {
      ad: p.get('ad') || ss('bb_ad') || '',
      variant: ss('bb_ad_variant') || window.bbAdVariant || '',
      source: p.get('utm_source') || ss('bb_utm_source') || '',
      medium: p.get('utm_medium') || '',
      campaign: p.get('utm_campaign') || ss('bb_utm_campaign') || '',
      content: p.get('utm_content') || ss('bb_utm_content') || '',
      term: p.get('utm_term') || '',
      click: click,
      ref: document.referrer || '',
      landing: (location.pathname + location.search).slice(0, 160)
    };
  }

  function push(type, name, value, n) {
    if (!type) return;
    var e = { t: Date.now(), type: String(type).slice(0, 20) };
    if (name != null) e.name = String(name).slice(0, 60);
    if (value != null) e.value = String(value).slice(0, 60);
    if (n != null && isFinite(+n)) e.n = +n;
    queue.push(e);
    // cart/checkout/wallet = the moments the cart is (about to be) turned into an order → re-assert
    // the attributes on THAT cart even if an earlier page already set them (fresh carts, races).
    ensureCartAttribute(type === 'cart' || type === 'checkout' || type === 'wallet');
    schedule();
  }

  function schedule() { if (flushTimer) return; flushTimer = setTimeout(flush, 4000); }
  function flush(useBeacon) {
    flushTimer = null;
    if (!queue.length) return;
    var payload = JSON.stringify({ vid: vid(), attribution: bbAttribution(), events: queue.splice(0, queue.length) });
    try {
      // sendBeacon always sends credentials, which the collector's wildcard CORS rejects —
      // unload batches were silently dropped (QA 2026-08-21). fetch keepalive survives unload
      // and stays uncredentialed, so the wildcard is fine.
      fetch(ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true }).catch(function () {});
    } catch (e) { /* telemetry must never break the storefront */ }
  }

  // Attach bb_vid to the cart so it lands on the order (order.note_attributes) → server stitch.
  // Cart attributes → order.note_attributes: bb_vid (journey stitch) + _ad/_ad_variant so EXPRESS
  // wallets (Shop Pay / Apple Pay / Google Pay / PayPal — they skip the checkout page) still arrive
  // attributed and join the A/B/C variant split. Re-asserted (force) on cart/checkout/wallet events —
  // the cart that exists at add-to-cart is the one an express order is built from.
  var forceAssertPending = false;
  function ensureCartAttribute(force) {
    if (attached && !force) return; attached = true;
    var post = function () {
      try {
        var a = bbAttribution();
        var attrs = { bb_vid: vid() };
        if (a.ad) attrs._ad = a.ad;
        if (a.variant) attrs._ad_variant = a.variant;
        // Cart-race rule (2026-08-25): serialised, gated on cart existence, no keepalive.
        if (window.bbCartExists && !window.bbCartExists()) {
          document.addEventListener('bb:cart-added', function () { if (window.bbQueueCartWrite) window.bbQueueCartWrite(attrs); }, { once: true });
        } else if (window.bbQueueCartWrite) { window.bbQueueCartWrite(attrs); }
        else fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: attrs }) }).catch(function () {});
      } catch (e) { /* ignore */ }
    };
    // Force re-asserts ride cart/checkout/wallet clicks — the same instant /cart/add fires.
    // A parallel update+add on a fresh/stale cart token can mint TWO carts, and if the
    // attributes-only one wins the cookie the shopper lands on an EMPTY cart after adding
    // (seen live 2026-08-21). Let the add win the token race; 1.2s later is still long
    // before any checkout completes. Coalesce bursts (several events fire per click).
    if (force) {
      if (forceAssertPending) return;
      forceAssertPending = true;
      setTimeout(function () { forceAssertPending = false; post(); }, 1200);
    } else {
      post();
    }
  }

  // Public API for the theme.
  window.bbTrack = function (type, name, value, n) { push(type, name, value, n); };

  // Auto: page view.
  push('page', null, (location.pathname || '/').slice(0, 200));

  // Auto: scroll-depth milestones (25/50/75/90%), once each.
  var hit = {};
  window.addEventListener('scroll', function () {
    var h = document.documentElement;
    var pct = Math.round(((h.scrollTop || document.body.scrollTop) + h.clientHeight) / (h.scrollHeight || 1) * 100);
    [25, 50, 75, 90].forEach(function (m) { if (pct >= m && !hit[m]) { hit[m] = 1; push('scroll', String(m)); } });
  }, { passive: true });

  // Flush on the way out.
  window.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') flush(true); });
  window.addEventListener('pagehide', function () { flush(true); });
})();
